/**
 * Session restore — called once on app boot.
 *
 * Uses a bare Axios client (no interceptors) to avoid circular imports and
 * to prevent the 401-retry interceptor from triggering during restoration.
 *
 * Flow on reload:
 *  1. Zustand rehydrates user + workshop from localStorage.
 *  2. accessToken is null (never persisted).
 *  3. This utility calls /workshop/auth/refresh-token to get a fresh access token.
 *  4. On success → token stored in memory, user stays logged in.
 *  5. On failure → refresh token expired, user is logged out cleanly.
 *  6. Sliding session: if the backend sends a new refresh-token header,
 *     it is persisted so the user is never logged out during daily usage.
 */

import axios from 'axios';

import { env } from '@config/env';

import { tokenStorage } from './token-storage';

// ─── Bare client for session restore ────────────────────────────────────────
// Must NOT share interceptors with httpClient (circular dep + infinite retry).

const sessionClient = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'app-type': 'Garage',
  },
});

interface RefreshResponse {
  status: number;
  data: { accessToken: string; refreshToken?: string | null };
}

export interface SessionRestoreCallbacks {
  onSuccess: (accessToken: string) => void;
  /** Called when refresh token is missing or expired. Clean up and redirect. */
  onFailure: () => void;
}

/**
 * Attempt to silently restore the access token using the stored refresh token.
 *
 * Sliding refresh: if the backend returns a new `refresh-token` response header
 * (issued when the current one expires within 24 hours), it is automatically
 * persisted so the user stays logged in indefinitely while actively using the app.
 */
export async function restoreSession({
  onSuccess,
  onFailure,
}: SessionRestoreCallbacks): Promise<void> {
  // Fast path: access token still cached in sessionStorage from earlier in
  // this tab session. Skip the network round-trip entirely — the JWT itself
  // carries an exp claim, so a stale token simply triggers the interceptor's
  // silent refresh on its next 401, which is the normal flow.
  const cachedAccessToken = tokenStorage.getAccessToken();
  if (cachedAccessToken) {
    onSuccess(cachedAccessToken);
    return;
  }

  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    onFailure();
    return;
  }

  try {
    const res = await sessionClient.post<RefreshResponse>(
      '/workshop/auth/refresh-token',
      {},
      { headers: { 'refresh-token': refreshToken } },
    );

    const newAccessToken = res.data.data.accessToken;

    // Cache new access token for the rest of this tab session.
    tokenStorage.setAccessToken(newAccessToken);

    // Sliding refresh: persist new refresh token if backend rotated it (within 48 h).
    const rotatedRefreshToken =
      res.data?.data?.refreshToken ?? (res.headers['refresh-token'] as string | undefined) ?? null;
    if (rotatedRefreshToken && rotatedRefreshToken !== 'null') {
      tokenStorage.setRefreshToken(rotatedRefreshToken);
    }

    onSuccess(newAccessToken);
  } catch {
    // Refresh token is invalid or expired → force logout.
    tokenStorage.removeRefreshToken();
    tokenStorage.removeAccessToken();
    onFailure();
  }
}
